import React, { useEffect, useState } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Wallet, Printer, Download, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AllLedgersReport = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate + 'T23:59:59').toISOString();
      const r = await api.get('/branch-ledger/reports/consolidated', { params });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, []);

  const branchRows = (data?.rows || []).filter((r) => r.member_type === 'branch');
  const memberRows = (data?.rows || []).filter((r) => r.member_type !== 'branch');

  const doPrint = () => {
    if (!data) return;
    const section = (title, rows) => `
      <h3 style="margin-top:16px">${title}</h3>
      <table class="w-full border">
        <thead><tr>
          <th>#</th><th>Name</th><th>Account</th><th>Branch</th>
          <th class="text-right">Entries</th>
          <th class="text-right">Debit</th><th class="text-right">Credit</th><th class="text-right">Balance</th>
        </tr></thead>
        <tbody>
          ${rows.map((r, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${r.member_name}</td>
              <td>${r.account_name}</td>
              <td>${r.branch_name || '—'}</td>
              <td class="text-right">${r.entry_count}</td>
              <td class="text-right">${fmt(r.total_debit)}</td>
              <td class="text-right">${fmt(r.total_credit)}</td>
              <td class="text-right">${fmt(r.balance)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;
    const html = buildPrintHTML(
      'All Ledgers — Branch & Member Consolidated',
      `
        <div class="text-sm mb-2">
          Date filter: ${startDate || '—'} to ${endDate || '—'}
          &nbsp;•&nbsp; Grand Debit: <b>${fmt(data.grand_total_debit)}</b>
          &nbsp;•&nbsp; Grand Credit: <b>${fmt(data.grand_total_credit)}</b>
          &nbsp;•&nbsp; Net Balance: <b>${fmt(data.grand_balance)}</b>
        </div>
        ${section('Branch Ledgers', branchRows)}
        ${section('Member Ledgers', memberRows)}
      `
    );
    openPrintWindow(html);
  };

  const doCsv = () => {
    if (!data) return;
    const header = ['Type', 'Name', 'Account', 'Branch', 'Entries', 'Debit', 'Credit', 'Balance'];
    const rows = (data.rows || []).map((r) => [
      r.member_type, r.member_name, r.account_name, r.branch_name || '',
      r.entry_count, r.total_debit, r.total_credit, r.balance,
    ]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `all-ledgers-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="all-ledgers-report">
      <div className="flex items-center gap-2 mb-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Reports
        </Button>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-emerald-700" /> All Ledgers Report
          </h1>
          <p className="text-sm text-slate-500 mt-1">Consolidated Branch + Member ledgers with grand-total Dr/Cr and net balance.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-xs">Start Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-9" />
          </div>
          <div>
            <Label className="text-xs">End Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-9" />
          </div>
          <Button onClick={fetchReport} disabled={loading} className="bg-emerald-700 hover:bg-emerald-800 text-white h-9">Apply</Button>
          <Button variant="outline" onClick={doPrint} disabled={!data} className="h-9"><Printer className="w-4 h-4 mr-1" /> Print</Button>
          <Button variant="outline" onClick={doCsv} disabled={!data} className="h-9"><Download className="w-4 h-4 mr-1" /> CSV</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div></div>
      ) : !data ? null : (
        <>
          {/* Grand totals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="text-xs uppercase font-semibold text-red-700">Grand Debit</div>
              <div className="text-2xl font-bold text-red-700 mt-1">{fmt(data.grand_total_debit)}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="text-xs uppercase font-semibold text-emerald-700">Grand Credit</div>
              <div className="text-2xl font-bold text-emerald-700 mt-1">{fmt(data.grand_total_credit)}</div>
            </div>
            <div className={`border rounded-xl p-4 ${data.grand_balance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className={`text-xs uppercase font-semibold ${data.grand_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Net Balance</div>
              <div className={`text-2xl font-bold mt-1 ${data.grand_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(data.grand_balance)}</div>
            </div>
          </div>

          {/* Branch Ledgers */}
          <Section title="Branch Ledgers" rows={branchRows} />
          {/* Member Ledgers */}
          <Section title="Member Ledgers" rows={memberRows} />
        </>
      )}
    </div>
  );
};

const Section = ({ title, rows }) => (
  <div className="mb-6">
    <h2 className="text-lg font-semibold text-slate-800 mb-2">{title} <span className="text-slate-400 text-sm">({rows.length})</span></h2>
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-emerald-700 text-white">
          <tr>
            <th className="px-3 py-2 text-left">#</th>
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Account</th>
            <th className="px-3 py-2 text-left">Branch</th>
            <th className="px-3 py-2 text-right">Entries</th>
            <th className="px-3 py-2 text-right">Debit</th>
            <th className="px-3 py-2 text-right">Credit</th>
            <th className="px-3 py-2 text-right">Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={8} className="px-3 py-6 text-center italic text-slate-400">No ledgers found for the selected filter.</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.account_id} className="hover:bg-slate-50" data-testid="ledger-row">
              <td className="px-3 py-2 text-slate-500">{i + 1}</td>
              <td className="px-3 py-2 font-medium text-slate-900">{r.member_name}</td>
              <td className="px-3 py-2 text-slate-700">{r.account_name}</td>
              <td className="px-3 py-2 text-slate-700">{r.branch_name || '—'}</td>
              <td className="px-3 py-2 text-right">{r.entry_count}</td>
              <td className="px-3 py-2 text-right text-red-700 font-medium">{fmt(r.total_debit)}</td>
              <td className="px-3 py-2 text-right text-emerald-700 font-medium">{fmt(r.total_credit)}</td>
              <td className={`px-3 py-2 text-right font-semibold ${r.balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(r.balance)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export default AllLedgersReport;
